import type {
  FulfillmentOrderListFilters,
  FulfillmentOrderSortDirection,
  FulfillmentOrderSortField,
} from "../types";

export type FulfillmentOrderListState = {
  page: number;
  pageSize: number;
  submittedFilters: FulfillmentOrderListFilters;
  sortField: FulfillmentOrderSortField;
  sortDirection: FulfillmentOrderSortDirection;
  sortCleared: boolean;
};

/** URL 只接纳当前列表支持的查询值，非法分页和排序回退默认值。 */
export function readFulfillmentOrderListState(params: URLSearchParams): FulfillmentOrderListState {
  const requestedPage = Number(params.get("page") ?? 1);
  const requestedSize = Number(params.get("pageSize") ?? 20);
  const sortField = params.get("sortField");
  const status = params.get("status");
  const sortCleared = params.get("sortCleared") === "1";
  return {
    page: Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1,
    pageSize: [10, 20, 50, 100].includes(requestedSize) ? requestedSize : 20,
    submittedFilters: normalizeFulfillmentOrderListFilters({
      q: params.get("q") ?? undefined,
      status: status === "draft" || status === "effective" ? status : undefined,
      supplierId: params.get("supplierId") ?? undefined,
      itineraryId: params.get("itineraryId") ?? undefined,
    }),
    sortField: !sortCleared && ["updated_at", "created_at", "current_amount", "order_no"].includes(sortField ?? "")
      ? sortField as FulfillmentOrderSortField : "updated_at",
    sortDirection: !sortCleared && params.get("sortDirection") === "asc" ? "asc" : "desc",
    sortCleared,
  };
}

/** 写入已提交查询并保留不属于本列表的参数，使进入详情后的返回可恢复原上下文。 */
export function writeFulfillmentOrderListState(params: URLSearchParams, state: FulfillmentOrderListState): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of ["q", "status", "supplierId", "itineraryId"]) next.delete(key);
  Object.entries(normalizeFulfillmentOrderListFilters(state.submittedFilters)).forEach(([key, value]) => next.set(key, value));
  next.set("page", String(state.page));
  next.set("pageSize", String(state.pageSize));
  next.set("sortField", state.sortCleared ? "updated_at" : state.sortField);
  next.set("sortDirection", state.sortCleared ? "desc" : state.sortDirection);
  if (state.sortCleared) next.set("sortCleared", "1");
  else next.delete("sortCleared");
  return next;
}

/** 服务端无时区时间按北京时间解释，带时区时间统一换算，列表省略秒及微秒。 */
export function formatFulfillmentDateTime(value?: string | null): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/u.test(value)) return "未记录时间";
  const normalized = value.replace(" ", "T").replace(/(\.\d{3})\d+/u, "$1");
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/u.test(normalized) ? normalized : `${normalized}+08:00`);
  if (Number.isNaN(date.getTime())) return "未记录时间";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(date);
}

/** 规范化列表筛选，空值不进入服务端查询或 query key。 */
export function normalizeFulfillmentOrderListFilters(
  filters: FulfillmentOrderListFilters,
): FulfillmentOrderListFilters {
  return Object.fromEntries(
    Object.entries(filters)
      .map(([key, value]) => [key, value?.trim()] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[1])),
  );
}

/** 构造稳定的服务端列表查询键。 */
export function buildFulfillmentOrderListQueryKey(
  page: number,
  pageSize: number,
  submittedFilters: FulfillmentOrderListFilters,
  sortField: FulfillmentOrderSortField,
  sortDirection: FulfillmentOrderSortDirection,
) {
  return [
    "fulfillmentOrder",
    "list",
    { page, pageSize, submittedFilters, sortField, sortDirection },
  ] as const;
}
